import { observeCustomSelects } from './components/custom-select.js';
import { initRegionalContacts } from './components/regional-contacts.js';
import { initBackgroundVideos } from './components/background-video.js';

observeCustomSelects();
initBackgroundVideos();

const header = document.querySelector('.site-header');
const headerToggle = header?.querySelector('.site-header__toggle');
const headerNavigation = header?.querySelector('.site-header__navigation');

const closeHeaderNavigation = () => {
  if (!header || !headerToggle || !headerNavigation) {
    return;
  }

  header.classList.remove('site-header--open');
  headerToggle.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('has-open-navigation');
};

if (header && headerToggle && headerNavigation) {
  header.classList.add('is-enhanced');
  headerToggle.addEventListener('click', () => {
    const isOpen = header.classList.toggle('site-header--open');
    headerToggle.setAttribute('aria-expanded', String(isOpen));
    document.body.classList.toggle('has-open-navigation', isOpen);
  });

  headerNavigation.addEventListener('click', (event) => {
    if (event.target.closest('a')) {
      closeHeaderNavigation();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && header.classList.contains('site-header--open')) {
      closeHeaderNavigation();
      headerToggle.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 1181px)').matches) {
      closeHeaderNavigation();
    }
  });
}

document.querySelectorAll('[data-region-capabilities]').forEach((section) => {
  const links = [...section.querySelectorAll('[data-capability-region]')];
  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const selected = link.dataset.capabilityRegion;
      links.forEach((item) => {
        const active = item.dataset.capabilityRegion === selected;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', String(active));
      });
      section.querySelectorAll('[data-capability-column]').forEach((cell) => {
        cell.classList.toggle('is-selected', cell.dataset.capabilityColumn === selected);
      });
    });
  });
});

const contactForms = document.querySelectorAll(
  '.section-contact-form__form, .legal-document__form, .section-cta__regions-form',
);

const regionGateways = document.querySelectorAll('[data-regions-gateway]');

regionGateways.forEach(initRegionalContacts);

const loadRegionsMap = (gateway) => {
  import('./regions-map.js')
    .then(({ initRegionsMap }) => initRegionsMap(gateway))
    .catch(() => {
      gateway.classList.add('has-static-map');
    });
};

if ('IntersectionObserver' in window) {
  const mapObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        mapObserver.unobserve(entry.target);
        loadRegionsMap(entry.target);
      }
    });
  }, { rootMargin: '250px' });
  regionGateways.forEach((gateway) => mapObserver.observe(gateway));
} else {
  regionGateways.forEach(loadRegionsMap);
}

const setContactRegion = (requestedRegion) => {
  const regionSelect = document.querySelector('#contact-general-region');

  if (!(regionSelect instanceof HTMLSelectElement) || !requestedRegion) {
    return false;
  }

  const matchingOption = [...regionSelect.options].find(
    (option) => option.value.toLowerCase() === requestedRegion.toLowerCase(),
  );

  if (!matchingOption) {
    return false;
  }

  regionSelect.value = matchingOption.value;
  regionSelect.dispatchEvent(new Event('change', { bubbles: true }));

  return true;
};

const requestedContactRegion = new URLSearchParams(window.location.search).get(
  'contact_region',
);

if (requestedContactRegion) {
  setContactRegion(requestedContactRegion);
}

document.querySelectorAll('[data-contact-region]').forEach((regionLink) => {
  regionLink.addEventListener('click', (event) => {
    const targetUrl = new URL(regionLink.href, window.location.href);
    const isCurrentContactPage =
      targetUrl.origin === window.location.origin
      && targetUrl.pathname === window.location.pathname;

    if (
      !isCurrentContactPage
      || !setContactRegion(regionLink.dataset.contactRegion || '')
    ) {
      return;
    }

    event.preventDefault();
    window.history.pushState({}, '', targetUrl);
    document.querySelector('#contact-general')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'start',
    });
  });
});

document.querySelectorAll('[data-contact-file-input]').forEach((fileInput) => {
  const fileValue = fileInput
    .closest('.section-contact-form__file-control')
    ?.querySelector('[data-contact-file-value]');

  if (!fileValue) {
    return;
  }

  const placeholder = fileValue.textContent;
  const syncFileValue = () => {
    fileValue.textContent = fileInput.files?.[0]?.name || placeholder;
  };

  fileInput.addEventListener('change', syncFileValue);
  fileInput.form?.addEventListener('reset', () => {
    window.setTimeout(syncFileValue, 0);
  });
});

const setContactFieldValidationMessage = (field, message) => {
  const customSelect = field.closest('[data-custom-select]');
  const customSelectValue = customSelect?.querySelector(
    '.custom-select__value',
  );
  const fileValue = field
    .closest('.section-contact-form__file-control')
    ?.querySelector('[data-contact-file-value]');

  if (customSelectValue) {
    if (!Object.hasOwn(customSelectValue.dataset, 'validationValue')) {
      customSelectValue.dataset.validationValue =
        customSelectValue.textContent?.trim() || '';
    }

    customSelectValue.textContent = message;
    return;
  }

  if (fileValue) {
    if (!Object.hasOwn(fileValue.dataset, 'validationValue')) {
      fileValue.dataset.validationValue = fileValue.textContent?.trim() || '';
    }

    fileValue.textContent = message;
    return;
  }

  if (
    field instanceof HTMLInputElement
    || field instanceof HTMLTextAreaElement
  ) {
    if (!Object.hasOwn(field.dataset, 'validationPlaceholder')) {
      field.dataset.validationPlaceholder =
        field.getAttribute('placeholder') || '';
    }

    field.setAttribute('placeholder', message);
  }
};

const restoreContactFieldValidationMessage = (field) => {
  const customSelect = field.closest('[data-custom-select]');
  const customSelectValue = customSelect?.querySelector(
    '.custom-select__value',
  );
  const fileValue = field
    .closest('.section-contact-form__file-control')
    ?.querySelector('[data-contact-file-value]');

  if (
    customSelectValue
    && Object.hasOwn(customSelectValue.dataset, 'validationValue')
  ) {
    const nativeSelect = customSelect.querySelector(
      '[data-custom-select-native]',
    );
    const selectedOption = nativeSelect?.selectedOptions?.[0];

    customSelectValue.textContent = nativeSelect?.value
      ? selectedOption?.textContent?.trim() || ''
      : customSelectValue.dataset.validationValue;
    delete customSelectValue.dataset.validationValue;
  }

  if (fileValue && Object.hasOwn(fileValue.dataset, 'validationValue')) {
    fileValue.textContent =
      field.files?.[0]?.name || fileValue.dataset.validationValue;
    delete fileValue.dataset.validationValue;
  }

  if (
    (field instanceof HTMLInputElement
      || field instanceof HTMLTextAreaElement)
    && Object.hasOwn(field.dataset, 'validationPlaceholder')
  ) {
    field.setAttribute('placeholder', field.dataset.validationPlaceholder);
    delete field.dataset.validationPlaceholder;
  }
};

const removeContactFormFeedback = (form) => {
  const container = form.parentElement;

  container
    ?.querySelectorAll(
      '.section-contact-form__notice, .legal-document__form-notice',
    )
    .forEach((notice) => notice.remove());

  if (!form.classList.contains('legal-document__form')) {
    form
      .querySelectorAll(
        'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]',
      )
      .forEach(restoreContactFieldValidationMessage);
  }

  form
    .querySelectorAll(
      '.section-contact-form__field-error, .custom-select__error, .legal-document__field-error',
    )
    .forEach((error) => error.remove());

  form.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
    field.removeAttribute('aria-invalid');

    const errorId = field.dataset.errorId || `${field.id}-error`;
    const describedBy = (field.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== errorId);

    if (describedBy.length) {
      field.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      field.removeAttribute('aria-describedby');
    }
  });
};

const showContactFormNotice = (form, message, status) => {
  const isLegal = form.classList.contains('legal-document__form');
  const notice = document.createElement('div');

  notice.className = isLegal
    ? `legal-document__form-notice legal-document__form-notice--${status}`
    : `section-contact-form__notice section-contact-form__notice--${status}`;
  notice.setAttribute('role', status === 'success' ? 'status' : 'alert');
  notice.textContent = message;
  form.before(notice);
};

const showContactFieldErrors = (form, fieldErrors) => {
  let firstInvalidField = null;

  Object.entries(fieldErrors).forEach(([fieldName, message]) => {
    const field = [...form.elements].find(
      (element) => element.name === fieldName,
    );

    if (!field || !field.id) {
      return;
    }

    const isLegal = form.classList.contains('legal-document__form');
    const error = document.createElement('span');
    const errorId = `${field.id}-error`;
    const customSelect = field.closest('[data-custom-select]');
    const customSelectTrigger = customSelect?.querySelector(
      '[data-custom-select-trigger]',
    );
    const fieldContainer = field.closest(
      isLegal
        ? '.legal-document__field'
        : '.section-contact-form__field, .section-contact-form__consent, .section-cta__regions-field, .section-cta__regions-select',
    );

    error.id = errorId;
    error.className = isLegal
      ? 'legal-document__field-error'
      : customSelect ? 'custom-select__error' : 'section-contact-form__field-error';
    error.textContent = message;
    field.setAttribute('aria-invalid', 'true');

    const describedBy = new Set(
      (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean),
    );
    describedBy.add(errorId);
    field.setAttribute('aria-describedby', [...describedBy].join(' '));

    if (customSelectTrigger) {
      customSelectTrigger.setAttribute('aria-invalid', 'true');
      customSelectTrigger.setAttribute('aria-describedby', [...describedBy].join(' '));
    }

    if (!isLegal) {
      setContactFieldValidationMessage(field, message);
    }

    fieldContainer?.append(error);
    firstInvalidField ||= customSelectTrigger || field;
  });

  firstInvalidField?.focus();
};

const clearContactFieldError = (form, field) => {
  if (!field.id || field.getAttribute('aria-invalid') !== 'true') {
    return;
  }

  const errorId = `${field.id}-error`;
  const error = form.querySelector(`#${errorId}`);
  const customSelectTrigger = field
    .closest('[data-custom-select]')
    ?.querySelector('[data-custom-select-trigger]');
  const describedBy = (field.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((id) => id && id !== errorId);

  if (!form.classList.contains('legal-document__form')) {
    restoreContactFieldValidationMessage(field);
  }

  error?.remove();
  field.removeAttribute('aria-invalid');

  if (describedBy.length) {
    field.setAttribute('aria-describedby', describedBy.join(' '));
  } else {
    field.removeAttribute('aria-describedby');
  }

  customSelectTrigger?.removeAttribute('aria-invalid');
  if (describedBy.length) {
    customSelectTrigger?.setAttribute('aria-describedby', describedBy.join(' '));
  } else {
    customSelectTrigger?.removeAttribute('aria-describedby');
  }

  const hasInvalidFields = form.querySelector(
    'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]',
  );
  const errorNotice = form.previousElementSibling;
  const errorNoticeSelector = form.classList.contains('legal-document__form')
    ? '.legal-document__form-notice--error'
    : '.section-contact-form__notice--error';

  if (!hasInvalidFields && errorNotice?.matches(errorNoticeSelector)) {
    errorNotice.remove();
  }
};

const contactSubmitContents = new WeakMap();
const setContactSubmitLoading = (submitButton, isLoading) => {
  if (!(submitButton instanceof HTMLButtonElement)) {
    return;
  }

  if (isLoading) {
    if (submitButton.dataset.loadingLabel) {
      contactSubmitContents.set(submitButton, [...submitButton.childNodes]);
      submitButton.textContent = submitButton.dataset.loadingLabel;
    }
    submitButton.classList.add('is-loading');
    submitButton.disabled = true;
    return;
  }

  if (contactSubmitContents.has(submitButton)) {
    submitButton.replaceChildren(...contactSubmitContents.get(submitButton));
    contactSubmitContents.delete(submitButton);
  }
  submitButton.classList.remove('is-loading');
  submitButton.disabled = false;
};

contactForms.forEach((form) => {
  const renewRequestId = () => {
    const field = form.querySelector('[name="contact_request_id"]');
    if (field && window.crypto?.randomUUID) {
      field.value = window.crypto.randomUUID();
    }
  };
  renewRequestId();
  form.noValidate = true;

  ['input', 'change'].forEach((eventName) => {
    form.addEventListener(eventName, (event) => {
      if (event.target instanceof HTMLInputElement
        || event.target instanceof HTMLSelectElement
        || event.target instanceof HTMLTextAreaElement
      ) {
        clearContactFieldError(form, event.target);
      }
    });
  });

  form.addEventListener('submit', async (event) => {
    const ajaxUrl = form.dataset.ajaxUrl;

    if (!ajaxUrl || !window.fetch || !window.FormData) {
      return;
    }

    event.preventDefault();
    removeContactFormFeedback(form);

    const submitButton = form.querySelector('[data-contact-submit]');
    setContactSubmitLoading(submitButton, true);
    form.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const payload = await response.json();
      const data = payload?.data || {};

      if (payload?.success) {
        showContactFormNotice(
          form,
          data.message || form.dataset.successMessage,
          'success',
        );
        form.reset();
        renewRequestId();
        form.querySelector('[name="contact_loaded_at"]').value = String(
          Math.floor(Date.now() / 1000),
        );
      } else {
        showContactFormNotice(
          form,
          data.message || form.dataset.errorMessage,
          'error',
        );
        showContactFieldErrors(form, data.fieldErrors || {});
      }
    } catch {
      showContactFormNotice(
        form,
        form.dataset.errorMessage,
        'error',
      );
    } finally {
      setContactSubmitLoading(submitButton, false);
      form.removeAttribute('aria-busy');
    }
  });
});
